import type {
  ProductDecorationRequest,
  MockupFileEntity,
  MockupItemImageEntity,
} from "../../services/mockupGeneration";
import { generateMockupForItem } from "../../services/mockupGeneration";
import { persistMockupPresetIfRequested } from "./persistMockupPreset";

/**
 * Status types for mockup generation UI state.
 */
export const MockupStatus = {
  IDLE: "idle",
  PROCESSING: "processing",
  ERROR: "error",
  WARNING: "warning",
  SUCCESS: "success",
} as const;

export type MockupStatusType = (typeof MockupStatus)[keyof typeof MockupStatus];

export interface MockupStatusState {
  type: MockupStatusType;
  message: string | null;
  jobId?: string | null;
}

export interface HandleMockupGenerationParams {
  imageSource: string;
  pickedLogoUrl: string;
  pickedLogoFileId: string | null;
  additionalInstructions: string;
  pickedProductDecoration: ProductDecorationRequest;
  saveUpdatesToDecorationInformation: boolean;
}

export interface MockupGenerationContext {
  itemId: string;
  item: Record<string, unknown>;
  dispatch: (action: unknown) => void;
}

export interface MockupGenerationCallbacks {
  onFinalizeUploadFile: (file: MockupFileEntity) => void;
  onFinalizeItemImages?: (
    itemImage: MockupItemImageEntity,
    itemImages: MockupItemImageEntity[]
  ) => void;
  onSelectFile?: (file: MockupFileEntity) => void;
}

export interface MockupGenerationSuccessResult {
  success: true;
  file: MockupFileEntity;
  warnings: string[];
  jobId: string | null;
}

export interface MockupGenerationErrorResult {
  success: false;
  errorMessage: string;
}

export interface MockupGenerationNoFileResult {
  success: true;
  file: null;
  warnings: [];
}

export type MockupGenerationResult =
  | MockupGenerationSuccessResult
  | MockupGenerationErrorResult
  | MockupGenerationNoFileResult;

/**
 * Validates the required inputs for mockup generation.
 * Returns an error message if validation fails, null if valid.
 */
export function validateMockupInputs(params: HandleMockupGenerationParams): string | null {
  const { imageSource, pickedLogoUrl, pickedProductDecoration } = params;

  if (!imageSource || !pickedLogoUrl || !pickedProductDecoration) {
    return "Please select an image, logo, and product decoration before generating a mockup.";
  }

  return null;
}

/**
 * Core mockup generation logic shared between SelectFilePopup and SelectItemImagesPopup.
 * 
 * This function handles:
 * - Persisting decoration updates if requested
 * - Generating the mockup via API
 * - Calling the provided callbacks on success
 * 
 * @returns A result object indicating success/failure and any warnings
 */
export async function executeMockupGeneration(
  params: HandleMockupGenerationParams,
  context: MockupGenerationContext,
  callbacks: MockupGenerationCallbacks
): Promise<MockupGenerationResult> {
  const {
    imageSource,
    pickedLogoUrl,
    pickedLogoFileId,
    additionalInstructions,
    pickedProductDecoration,
    saveUpdatesToDecorationInformation,
  } = params;

  const { itemId, item, dispatch } = context;
  const { onFinalizeUploadFile, onFinalizeItemImages, onSelectFile } = callbacks;

  try {
    const persistResult = await persistMockupPresetIfRequested({
      dispatch,
      itemId,
      item,
      pickedDecoration: pickedProductDecoration,
      logoFileId: pickedLogoFileId,
      saveUpdates: saveUpdatesToDecorationInformation,
    });
    const warnings = persistResult?.warnings || [];

    const result = await generateMockupForItem({
      itemId,
      productImageUrl: imageSource,
      logoImageUrl: pickedLogoUrl,
      instructions: additionalInstructions,
      productDecorations: pickedProductDecoration,
    });

    const uploadedFile = result.file;
    if (!uploadedFile) {
      return { success: true, file: null, warnings: [] };
    }

    onFinalizeUploadFile(uploadedFile);

    if (result.item_images && result.item_images.length && onFinalizeItemImages) {
      onFinalizeItemImages(result.item_image, result.item_images);
    }

    onSelectFile?.(uploadedFile);

    return { success: true, file: uploadedFile, warnings, jobId: result.job_id ?? null };
  } catch (error) {
    console.error(error);
    const errorMessage =
      (error as Error)?.message ||
      String(error) ||
      "An error occurred while generating the mockup.";
    return { success: false, errorMessage };
  }
}

export function safeString(value: unknown): string {
  return String(value ?? "");
}

export function normalizeString(value: unknown): string {
  return safeString(value)
    .toLowerCase()
    .replace(/\s+/g, " ")
    .trim();
}

export function hasValue(value: unknown): boolean {
  if (typeof value === "string") return value.trim().length > 0;
  if (typeof value === "number") return true;
  return false;
}

export function isObject(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null;
}

export function isRecordArray(value: unknown): value is Array<Record<string, unknown>> {
  return Array.isArray(value) && value.every((v) => isObject(v));
}

export function hasExtDecorationId(extId: string | number | null | undefined): boolean {
  if (typeof extId === "string") return extId.trim().length > 0;
  if (typeof extId === "number") return true;
  return false;
}

export function readString(value: unknown): string | null {
  return (typeof value === "string" && value.trim().length > 0) ? value : null;
}

export function readNumber(value: unknown): number | null {
  return typeof value === "number" ? value : null;
}

export interface SelectOption {
  value: string;
  label: string;
}

export interface ParentContext {
  parent_id: string;
  parent_type: "JOB" | "SHOP" | "ITEM";
}

/**
 * Resolves the parent context based on available IDs.
 * Priority: Job > Shop > Item
 * 
 * @param args - Object containing optional job_id, shop_id, and required item_id
 * @returns The resolved parent context
 */
export function resolveParentContext(args: {
  job_id?: string | null;
  shop_id?: string | null;
  item_id: string;
}): ParentContext {
  const { job_id, shop_id, item_id } = args;

  if (job_id) {
    return { parent_id: job_id, parent_type: "JOB" };
  }

  if (shop_id) {
    return { parent_id: shop_id, parent_type: "SHOP" };
  }

  return { parent_id: item_id, parent_type: "ITEM" };
}

/**
 * Represents a selectable decoration option in the mockup generator UI.
 */
export interface MockupDecorationOption {
  value: string;
  label: string;
  decoration: ProductDecorationRequest;
  artworkImage?: string | null;
  artworkFileId?: string | null;
  source: "existing" | "ps-config";
}

/**
 * Configuration for building a decoration option.
 */
export interface DecorationOptionConfig {
  id: string;
  name: string;
  locationId?: string;
  locationName: string;
  itemDecorationId?: string | null;
  imprintId?: string | null;
  imprintName?: string | null;
  extDecorationId?: string | number | null;
  extLocationId?: string | number | null;
  source: "existing" | "ps-config";
  artwork?: { image: string | null; fileId: string | null };
}

/**
 * Base builder for decoration options that handles common transformation logic.
 * 
 * @param config - Configuration for building the option
 * @returns A standardized MockupDecorationOption
 */
export function buildDecorationOption(config: DecorationOptionConfig): MockupDecorationOption {
  const {
    id,
    name,
    locationId,
    locationName,
    itemDecorationId,
    imprintId,
    imprintName,
    extDecorationId,
    extLocationId,
    source,
    artwork,
  } = config;

  const resolvedName =
    safeString(name).trim()
    || safeString(id).trim()
    || "Decoration";

  const decoration: ProductDecorationRequest = {
    decoration_name: resolvedName,
    location_name: safeString(locationName),
    ps_decoration_id: safeString(id),
    ...(locationId && { item_location_id: locationId }),
    ...(itemDecorationId && { item_decoration_id: itemDecorationId }),
    ...(imprintId && { imprint_id: imprintId }),
    ...(imprintName && { imprint_name: imprintName }),
    ...(extDecorationId != null && { ext_decoration_id: extDecorationId }),
    ...(extLocationId != null && { ext_location_id: extLocationId }),
  };

  return {
    value: safeString(id),
    label: resolvedName,
    decoration,
    artworkImage: artwork?.image ?? null,
    artworkFileId: artwork?.fileId ?? null,
    source,
  };
}

